import { useCallback, useEffect, useState } from "react";
import AppHeader from "@/components/AppHeader";
import EditProjectDialog from "@/components/EditProjectDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { clearProjectCache } from "@/components/ProjectPicker";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  FolderOpen,
  Pencil,
  Archive,
  ArchiveRestore,
} from "lucide-react";

export default function AdminProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // project being edited
  const [adding, setAdding] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [toggling, setToggling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/projects", {
        params: { include_inactive: true },
      });
      setProjects(data.projects || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load projects.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const confirmToggleStatus = async () => {
    if (!statusTarget) return;
    setToggling(true);
    try {
      await api.patch(`/admin/projects/${statusTarget.id}/status`, {
        is_active: !statusTarget.is_active,
      });
      const action = !statusTarget.is_active ? "restored" : "archived";
      toast.success(`${statusTarget.name} ${action}`);
      clearProjectCache();
      setStatusTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update project.");
    } finally {
      setToggling(false);
    }
  };

  const active = projects.filter((p) => p.is_active);
  const archived = projects.filter((p) => !p.is_active);

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main
        className="max-w-7xl mx-auto px-4 sm:px-6 py-8"
        data-testid="admin-projects-page"
      >
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Projects</h2>
            <p className="text-sm text-slate-500 mt-1">
              Group hours by project so you can report on what each freelancer worked on.
            </p>
          </div>
          <button
            onClick={() => setAdding(true)}
            data-testid="add-project-button"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold transition shadow-sm"
            style={{
              background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
            }}
          >
            <Plus size={16} />
            Add project
          </button>
        </div>

        {error && (
          <div className="mb-4 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 flex items-center justify-center">
            <Loader2 className="animate-spin text-slate-400" size={20} />
          </div>
        ) : projects.length === 0 ? (
          <EmptyState onAdd={() => setAdding(true)} />
        ) : (
          <div className="space-y-6">
            <ProjectSection
              title="Active"
              count={active.length}
              projects={active}
              onEdit={setEditing}
              onToggle={setStatusTarget}
            />
            {archived.length > 0 && (
              <ProjectSection
                title="Archived"
                count={archived.length}
                projects={archived}
                onEdit={setEditing}
                onToggle={setStatusTarget}
                muted
              />
            )}
          </div>
        )}
      </main>

      <EditProjectDialog
        open={adding || !!editing}
        project={editing}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={() => {
          load();
        }}
      />

      <ConfirmDialog
        open={!!statusTarget}
        onClose={() => !toggling && setStatusTarget(null)}
        onConfirm={confirmToggleStatus}
        confirming={toggling}
        title={
          statusTarget?.is_active ? "Archive project?" : "Restore project?"
        }
        description={
          statusTarget
            ? statusTarget.is_active
              ? `${statusTarget.name} will be hidden from new entry pickers. Existing entries keep their project assignment, and you can restore anytime.`
              : `${statusTarget.name} will be available again in entry pickers.`
            : ""
        }
        confirmLabel={statusTarget?.is_active ? "Archive" : "Restore"}
        danger={!!statusTarget?.is_active}
      />
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
      <div
        className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
        style={{ background: `${BRAND.teal}12` }}
      >
        <FolderOpen style={{ color: BRAND.teal }} />
      </div>
      <h4 className="mt-3 text-sm font-semibold text-slate-700">
        No projects yet
      </h4>
      <p className="mt-1 text-xs text-slate-500 max-w-sm mx-auto">
        Add your first project to start grouping freelancer hours by client or
        initiative. Existing entries can be reassigned later.
      </p>
      <button
        onClick={onAdd}
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold transition"
        style={{
          background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
        }}
      >
        <Plus size={16} />
        Add your first project
      </button>
    </div>
  );
}

function ProjectSection({ title, count, projects, onEdit, onToggle, muted }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">
          {title}
        </h3>
        <span className="text-xs text-slate-400">{count}</span>
      </div>
      <div
        className={`bg-white rounded-2xl border border-slate-200 overflow-hidden ${
          muted ? "opacity-90" : ""
        }`}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <th className="text-left font-semibold px-4 py-3">Project</th>
                <th className="text-left font-semibold px-4 py-3">Client</th>
                <th className="text-right font-semibold px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-slate-100 hover:bg-slate-50/50 transition"
                  data-testid={`project-row-${p.id}`}
                >
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: p.color }}
                      />
                      <span className="font-medium text-slate-800">{p.name}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {p.client || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => onEdit(p)}
                        data-testid={`edit-project-${p.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border text-slate-600 border-slate-200 hover:bg-slate-50 transition"
                      >
                        <Pencil size={13} />
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                      <button
                        onClick={() => onToggle(p)}
                        data-testid={`toggle-project-${p.id}`}
                        className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border transition ${
                          p.is_active
                            ? "text-amber-700 border-amber-200 hover:bg-amber-50"
                            : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        }`}
                      >
                        {p.is_active ? (
                          <Archive size={13} />
                        ) : (
                          <ArchiveRestore size={13} />
                        )}
                        {p.is_active ? "Archive" : "Restore"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
